import { useCurrentWorkspace } from '@gripp/shared-logic';
import {
  SlideAction,
  SlideLayout,
  type ColumnData,
  type SlideLayoutLocationState,
  useSlideLayoutSafe,
  ViewMode,
} from '@gripp/shared-ui';
import { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { NewAlertHeaderAction } from '../../shared/components/alerts/newAlertHeaderAction';
import { AssetRouteProvider } from '../assetRouteContext';
import { AlertPanel } from './alert-panel';
import { AssetRoutineSubmitColumn } from './asset-routine-submit-column';
import { AssetRoutineSubmitHeader } from './asset-routine-submit-header';

type RoutineAlertsLocationState = SlideLayoutLocationState &
  Record<string, unknown>;

const toRoutineAlertsLocationState = (
  state: unknown
): RoutineAlertsLocationState | null =>
  state && typeof state === 'object' && !Array.isArray(state)
    ? (state as RoutineAlertsLocationState)
    : null;

type RoutineAlertsNewAlertHeaderActionProps = {
  accessibilityLabel: string;
  backState: Record<string, unknown> | undefined;
  getStateWithTransition: (transition: SlideAction) => Record<string, unknown>;
};

const RoutineAlertsNewAlertHeaderAction = ({
  accessibilityLabel,
  backState,
  getStateWithTransition,
}: RoutineAlertsNewAlertHeaderActionProps) => {
  const slideLayout = useSlideLayoutSafe();

  return (
    <NewAlertHeaderAction
      accessibilityLabel={accessibilityLabel}
      onBeforeNavigate={() => {
        slideLayout?.prepareSlideTransition(SlideAction.NEXT);
      }}
      getNavigationState={() =>
        slideLayout ? getStateWithTransition(SlideAction.NEXT) : backState
      }
    />
  );
};

export const AssetRoutineAlerts = () => {
  const { assetId, routineId, issueId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const currentWorkspace = useCurrentWorkspace();
  const locationState = toRoutineAlertsLocationState(location.state);
  const initialTransition =
    locationState?.slideLayoutInitialTransition === SlideAction.NEXT ||
    locationState?.slideLayoutInitialTransition === SlideAction.PREV
      ? locationState.slideLayoutInitialTransition
      : undefined;
  const backState = (() => {
    if (!locationState) return undefined;
    const { slideLayoutInitialTransition: _dropInitialTransition, ...rest } =
      locationState;
    return Object.keys(rest).length > 0 ? rest : undefined;
  })();
  const getStateWithTransition = (transition: SlideAction) => ({
    ...(backState ?? {}),
    slideLayoutInitialTransition: transition,
  });
  const backGuardRef = useRef<
    ((onProceed: () => void, onCancel?: () => void) => void) | null
  >(null);

  if (!assetId || !routineId) {
    return null;
  }

  const backButtonGuard = (): Promise<boolean> => {
    return new Promise<boolean>((resolve) => {
      if (!backGuardRef.current) {
        resolve(true);
        return;
      }
      backGuardRef.current(
        () => resolve(true),
        () => resolve(false)
      );
    });
  };

  const leftColumn: ColumnData = {
    title: 'routines.editRoutines.editRoutinesButton',
    mainComponent: (
      <AssetRoutineSubmitColumn assetId={assetId} backGuardRef={backGuardRef} />
    ),
    showBackButton: true,
    disableScroll: true,
    headerComponent: (
      <AssetRoutineSubmitHeader assetId={assetId} routineId={routineId} />
    ),
    backButtonTransition: SlideAction.PREV,
    backButtonTransitionBehavior: 'concurrent',
    backButtonTransitionExitColumn: 'both',
    backButtonGuard,
    onBackButtonPress: () =>
      navigate('../../..', {
        relative: 'path',
        state: getStateWithTransition(SlideAction.PREV),
      }),
  };

  const rightColumn: ColumnData = {
    title: 'alerts.alerts',
    mainComponent: <AlertPanel assetId={assetId} routineId={routineId} />,
    headerComponent: currentWorkspace ? (
      <RoutineAlertsNewAlertHeaderAction
        accessibilityLabel={t('alerts.setAlert')}
        backState={backState}
        getStateWithTransition={getStateWithTransition}
      />
    ) : undefined,
    backButtonTransition: SlideAction.PREV,
    backButtonTransitionBehavior: 'concurrent',
    onBackButtonPress: () =>
      navigate('..', {
        relative: 'path',
        state: getStateWithTransition(SlideAction.PREV),
      }),
    showBackButton: true,
    showHeader: true,
    disableScroll: true,
  };

  return (
    <AssetRouteProvider
      value={{
        basePath: issueId
          ? `/activities/${issueId}/asset/${assetId}`
          : `/assets/${assetId}`,
        listPath: issueId ? '/activities' : '/assets',
        isWorkspaceMode: false,
        issueId: issueId ?? undefined,
      }}
    >
      <SlideLayout
        leftColumn={leftColumn}
        rightColumn={rightColumn}
        viewMode={ViewMode.DUAL}
        initialTransition={initialTransition}
      />
    </AssetRouteProvider>
  );
};
